import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { computeCrawlerHealth, CrawlerHealth } from '../../modules/manifold/crawler-health';
import { IPublisherCrawlSummary, IPaywallPublisherCrawlUrl, IManifoldCrawlerStatusResponse, IManifoldLogResponse, ICrawlerDailyExtracted, IManifoldQueueResponse, ICrawlerSnapshotResponse } from '@app/models';

@Injectable({
  providedIn: 'root',
})
export class ManifoldService {
  private showLocalhostSubject = new BehaviorSubject<boolean>(localStorage.getItem('showLocalhost') === 'true');
  showLocalhost$ = this.showLocalhostSubject.asObservable();

  private queuePausedSubject = new BehaviorSubject<boolean>(false);
  queuePaused$ = this.queuePausedSubject.asObservable();

  // Last crawler-status fetch (from whoever asked: nav poll or the /manifold page). `failed` = the call errored.
  private crawlerStatusState = new BehaviorSubject<{ response: IManifoldCrawlerStatusResponse | null; failed: boolean }>({
    response: null,
    failed: false,
  });

  /**
   * The one crawler-health verdict shared by the Monitoring nav badge and the /manifold banner. Re-emits whenever a
   * status fetch lands, the localhost filter flips, or the queue is paused/resumed.
   */
  crawlerHealth$: Observable<CrawlerHealth> = combineLatest([this.crawlerStatusState, this.showLocalhost$, this.queuePaused$]).pipe(
    map(([{ response, failed }, showLocalhost, paused]) => {
      if (paused) return 'paused';
      if (failed) return 'red'; // can't reach the status endpoint -> assume the worst
      let crawlers = (response?.crawlers || []).filter((c) => !!c.scraperId);
      if (!showLocalhost) {
        crawlers = crawlers.filter((c) => !c.scraperId.toLowerCase().includes('localhost'));
      }
      return computeCrawlerHealth(crawlers, response?.latestCommit, false);
    })
  );

  constructor(private http: HttpClient) { }

  setShowLocalhost(value: boolean) {
    localStorage.setItem('showLocalhost', String(value));
    this.showLocalhostSubject.next(value);
  }

  getCrawlSummary(hours: number): Observable<IPublisherCrawlSummary[]> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/crawl-summary/last/${hours}`;
    return this.http.get<IPublisherCrawlSummary[]>(url);
  }

  getCrawlHistory(publisher: string, limit = 100): Observable<IPaywallPublisherCrawlUrl[]> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/crawl-history`;
    return this.http.get<IPaywallPublisherCrawlUrl[]>(url, {
      params: { publisher, limit: limit.toString() }
    });
  }

  getCrawlerStatus(): Observable<IManifoldCrawlerStatusResponse> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/crawlers/status`;
    return this.http.get<IManifoldCrawlerStatusResponse>(url).pipe(
      tap({
        next: (response) => this.crawlerStatusState.next({ response, failed: false }),
        error: () => this.crawlerStatusState.next({ response: null, failed: true }),
      })
    );
  }

  getCrawlerSnapshot(scraperId: string): Observable<ICrawlerSnapshotResponse> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/crawlers/${encodeURIComponent(scraperId)}/snapshot`;
    return this.http.get<ICrawlerSnapshotResponse>(url);
  }

  /** "Forget this crawler": deletes the scraper's log history + failover liveness row on Discovery. */
  deleteScraper(scraperId: string): Observable<void> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/crawlers/${encodeURIComponent(scraperId)}`;
    return this.http.delete<void>(url);
  }

  getQueue(): Observable<IManifoldQueueResponse> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/queue`;
    return this.http.get<IManifoldQueueResponse>(url);
  }

  emptyQueue(domain?: string | null, before?: string | null): Observable<void> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/queue`;
    const body: { domain?: string; before?: string } = {};
    if (domain) { body.domain = domain; }
    if (before) { body.before = before; }
    return this.http.delete<void>(url, { body });
  }

  getQueuePausedState(): Observable<boolean> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/next-url/paused`;
    return this.http.get<boolean>(url).pipe(
      tap(paused => this.queuePausedSubject.next(paused))
    );
  }

  getLogs(page: number, size: number, url?: string, event?: string): Observable<IManifoldLogResponse> {
    const apiUrl = `${environment.DISCOVERY_BASE_URL}/v1/manifold/logs`;
    const params: Record<string, string> = { page: page.toString(), size: size.toString() };
    if (url) { params['url'] = url; }
    if (event) { params['event'] = event; }
    return this.http.get<IManifoldLogResponse>(apiUrl, { params });
  }

  getDailyExtracted(days = 7): Observable<ICrawlerDailyExtracted[]> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/logs/daily-extracted`;
    return this.http.get<ICrawlerDailyExtracted[]>(url, { params: { days: days.toString() } });
  }

  toggleQueuePause(): Observable<boolean> {
    const url = `${environment.DISCOVERY_BASE_URL}/v1/manifold/next-url/pause`;
    return this.http.patch<boolean>(url, null).pipe(
      tap(paused => this.queuePausedSubject.next(paused))
    );
  }
}
